<template>
  <div
    class="search-input"
    data-pendo-ds="search_input"
    :style="{ width, 'justify-content': expandable && alignRight ? 'flex-end' : 'flex-start'}"
  >
    <div
      class="search-input__inner"
      :class="{ collapsed: expandable && collapsed, focused, disabled }"
      :style="{ width: expandable && collapsed ? '16px' : '100%' }"
      @mouseleave="!focused && !value && setCollapsed(true)"
      @mouseover="setCollapsed(false)"
    >
      <div
        class="search-input__icon-container"
        :class="{ disabled }"
        @click="$refs.searchInput.focus()"
      >
        <BaseIcon
          font-size="16px"
          icon="fa-regular fa-search"
        />
      </div>
      <div class="search-input__input-clear-container cr-align-center">
        <div class="search-input__input-container">
          <input
            :ref="'searchInput'"
            aria-label="search"
            class="search-input__input label-m-regular"
            :class="{ collapsed: expandable && collapsed }"
            :data-testid="inputDataTestid"
            :disabled="disabled"
            :placeholder="placeholder"
            :value="myValue"
            @blur="setFocused(false)"
            @focus="setFocused(true)"
            @input="updateValue"
          >
        </div>
        <div
          v-show="!(expandable && collapsed)"
          class="search-input__clear-container"
        >
          <BaseButton
            v-if="myValue.length > 0"
            :data-testid="inputDataTestid + '-clear'"
            emphasis="low"
            icon="fa-regular fa-close"
            theme="mono"
            @click="clear()"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, defineComponent } from 'vue'
import debounce from 'lodash/debounce'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faSearch, faClose } from '@fortawesome/pro-regular-svg-icons'

library.add(faSearch, faClose)

import BaseIcon from './BaseIcon.vue'
import BaseButton from './BaseButton.vue'

export default defineComponent({
  name: 'SearchInput',
  components: {
    BaseIcon,
    BaseButton,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Search',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    width: {
      type: String,
      default: '100%',
    },
    expandable: {
      type: Boolean,
      default: false,
    },
    alignRight: {
      type: Boolean,
      default: false,
    },
    debounceMs: {
      type: Number,
      default: 275,
    },
    inputDataTestid: {
      type: String,
      default: 'search-input',
    },
  },
  emits: ['update:value'],

  setup(props, { emit }) {
    const myValue = ref(props.value)
    const collapsed = ref(!props.value)
    const focused = ref(false)
    const searchInput = ref(null)

    const updateValue = debounce((event) => {
      myValue.value = event.target.value
      emit('update:value', event.target.value)
    }, props.debounceMs)

    const clear = () => {
      myValue.value = ''
      updateValue.cancel()
      searchInput.value?.focus()
      emit('update:value', '')
    }

    const setCollapsed = (state) => {
      if (!props.disabled) {
        collapsed.value = state
      }
    }

    const setFocused = (state) => {
      focused.value = state
      if (!myValue.value) {
        setCollapsed(!state)
      }
    }

    watch(() => props.value, (newValue) => {
      myValue.value = newValue
    })

    return {
      myValue,
      collapsed,
      focused,
      searchInput,
      updateValue,
      clear,
      setCollapsed,
      setFocused,
    }
  },
})
</script>

<style scoped lang="scss">
.search-input {
  display: flex;
  max-width: '100%'
}

.search-input__inner {
  padding: var(--space-2) var(--space-3);
  border-radius: var(--cr-br-round);
  box-shadow: var(--cr-ds-small);
  display: flex;
  height: 20px;
  transition: width 0.3s ease-in-out;
  box-sizing: content-box;
  border: 1px solid var(--blueberry-200);

  &.collapsed {
    transition: width 0.2s ease-in-out;
  }

  &.focused {
    border-color: var(--blueberry-1000);
  }

  &.disabled {
    border-color: var(--pepper-300);
    background-color: var(--pepper-200);
    cursor: not-allowed;
  }
}

.search-input__icon-container {
  display: flex;
  align-items: center;
  width: 20px;
  color: var(--blueberry-1000);
  cursor: pointer;

  &.disabled {
    color: var(--pepper-500);
    cursor: not-allowed
  }
}

.search-input__input-container {
  min-height: 100%;
  border-radius: var(--cr-br-round);
  width: 100%;
  padding-left: var(--space-2)
}

.search-input__input {
  box-sizing: border-box;
  display: block;
  margin: 0;
  width: 100%;
  line-height: 20px;
  border: none;
  outline: none;
  opacity: 1;

  &.collapsed {
    opacity: 0;
    transition: opacity 0s;
  }

  &:disabled {
    cursor: not-allowed;
  }

  &::placeholder {
    color: var(--pepper-600);
  }
}

.search-input__clear-container {
  align-items: center;
  display: flex;
  justify-content: center;
  width: 20px;

  .base-button {
    padding: var(--space-0);
  }
}

.search-input__input-clear-container {
  display: flex;
  position: relative;
  max-width: 100%;
  width: 100%;
  transition: width 0.3s ease-in-out;

  &.focused {
    border-color: var(--blueberry-1000);
  }

  &.disabled {
    color: var(--pepper-500);
    border: 1px solid var(--pepper-300);
    background-color: var(--pepper-200);
  }

  &.collapsed {
    transition: width 0s;
  }

  &.disabled .left-search-icon {
    color: var(--pepper-500);
  }

  &.disabled .close-icon {
    pointer-events: none;
  }
}
</style>
